import {
  PaginatedList,
  PaginatedQueryParams,
  PaginationParams,
} from "../types/pagination";
import {
  Shop,
  CreateShopDto,
  UpdateShopDto,
  ShopImportResult,
  ShopCreateResult,
  ShopRequest,
  ShopAudit,
  CheckShopResult,
} from "../types/shop";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

const shopsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getShops: builder.query<Shop[], number | undefined>({
      query: (preference) => ({
        url: "shops",
        params: preference !== undefined ? { preference } : {},
      }),
      transformResponse: unwrapApiResponse,
      providesTags: ["Shops"],
    }),
    getShopsPaginated: builder.query<PaginatedList<Shop>, PaginationParams>({
      query: (params) => ({
        url: "shops",
        params: { ...params, paginated: true },
      }),
      transformResponse: unwrapApiResponse,
      providesTags: ["Shops"],
    }),
    getShopsPaginatedWithSearch: builder.query<
      PaginatedList<Shop>,
      PaginatedQueryParams
    >({
      query: (queryParams) => {
        const params: any = {
          pageNumber: queryParams.pageNumber,
          pageSize: queryParams.pageSize,
          paginated: true,
        };

        if (
          queryParams.filters &&
          Object.keys(queryParams.filters).length > 0
        ) {
          Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              // Map frontend field names to backend filter keys
              const backendKey = key === 'preference' ? 'Preference' : key;
              params[`filters[${backendKey}]`] = value;
            }
          });
        }

        return {
          url: "shops",
          params,
        };
      },
      transformResponse: unwrapApiResponse,
      providesTags: ["Shops"],
    }),
    getActiveShops: builder.query<Shop[], void>({
      query: () => "shops/active",
      transformResponse: unwrapApiResponse,
      providesTags: ["Shops"],
    }),
    getShopById: builder.query<Shop, number>({
      query: (id) => `shops/${id}`,
      transformResponse: unwrapApiResponse,
      providesTags: (result, error, id) => [{ type: "Shops", id }],
    }),
    createShop: builder.mutation<ShopCreateResult, CreateShopDto>({
      query: (shop) => ({
        url: "shops",
        method: "POST",
        body: shop,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["Shops"],
    }),
    updateShop: builder.mutation<Shop, { id: number } & UpdateShopDto>({
      query: ({ id, ...shop }) => ({
        url: `shops/${id}`,
        method: "PUT",
        body: shop,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (_result, _error, { id }) => [
        "Shops",
        { type: "Shops", id },
      ],
    }),
    deleteShop: builder.mutation<void, number>({
      query: (id) => ({
        url: `shops/${id}`,
        method: "DELETE",
      }),
      transformResponse: () => undefined,
      invalidatesTags: ["Shops"],
    }),
    importShops: builder.mutation<ShopImportResult, FormData>({
      query: (formData) => ({
        url: "shops/import",
        method: "POST",
        body: formData,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["Shops"],
    }),
    getShopRequests: builder.query<ShopRequest[], { status?: number; requestedByUserId?: number; tenantId?: number } | void>({
      query: (params) => {
        const queryParams: any = {};
        if (params?.status !== undefined) {
          queryParams.status = params.status;
        }
        if (params?.requestedByUserId !== undefined) {
          queryParams.requestedByUserId = params.requestedByUserId;
        }
        if (params?.tenantId !== undefined) {
          queryParams.tenantId = params.tenantId;
        }
        return {
          url: "shops/requests",
          params: queryParams,
        };
      },
      transformResponse: unwrapApiResponse,
      providesTags: ["Shops"],
    }),
    approveShopRequest: builder.mutation<ShopRequest, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `shops/requests/${id}/approve`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["Shops"],
    }),
    rejectShopRequest: builder.mutation<ShopRequest, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `shops/requests/${id}/reject`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["Shops"],
    }),
    checkShop: builder.query<CheckShopResult, { address: string; name?: string }>({
      query: ({ address, name }) => ({
        url: "shops/check",
        params: { address, name },
      }),
      transformResponse: unwrapApiResponse,
    }),
    requestShopPreferenceChange: builder.mutation<ShopRequest, { id: number; targetPreference: number; reason?: string }>({
      query: ({ id, targetPreference, reason }) => ({
        url: `shops/${id}/preference-request`,
        method: "POST",
        body: { targetPreference, reason },
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["Shops"],
    }),
    getShopHistory: builder.query<ShopAudit[], number>({
      query: (id) => ({
        url: `shops/${id}/history`,
      }),
      transformResponse: unwrapApiResponse,
    }),
  }),
});

export const {
  useGetShopsQuery,
  useGetShopsPaginatedQuery,
  useGetShopsPaginatedWithSearchQuery,
  useGetShopByIdQuery,
  useCreateShopMutation,
  useUpdateShopMutation,
  useDeleteShopMutation,
  useImportShopsMutation,
  useGetShopRequestsQuery,
  useApproveShopRequestMutation,
  useRejectShopRequestMutation,
  useCheckShopQuery,
  useLazyCheckShopQuery,
  useRequestShopPreferenceChangeMutation,
  useGetShopHistoryQuery,
  useLazyGetShopHistoryQuery,
} = shopsApi;
